import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useInvitationRegistration } from '../hooks/useInvitationRegistration';
import { useInvitations } from '../hooks/useInvitations';
import type { UserRegistrationDetails } from '../types/Registration';
import type { Invite } from '../types/User';
import type { UserType } from '../types/User';

interface InvitationContextType {
  // Invitation fetching and registration
  fetchInvitation: (token: string) => Promise<{ 
    success: boolean; 
    invitation?: Invite; 
    error?: string;
  }>;
  
  registerAndAcceptInvitation: (userDetails: UserRegistrationDetails, invitationToken: string) => Promise<{ 
    success: boolean; 
    token?: string; 
    error?: string;
    message?: string;
  }>;
  // User type and validation
  fetchUserTypes: () => Promise<{ success: boolean; types?: UserType[]; error?: string }>;
  // Cached user types (may be null before load)
  userTypes: UserType[] | null;
  // Force refresh of user types
  refreshUserTypes: () => Promise<{ success: boolean; types?: UserType[]; error?: string }>;
  checkUserExists: (email: string) => Promise<{ exists: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined);

export const useInvitationContext = () => {
  const ctx = useContext(InvitationContext);
  if (!ctx) throw new Error('useInvitationContext must be used within an InvitationProvider');
  return ctx;
};

interface InvitationProviderProps {
  children: ReactNode;
}

export const InvitationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const registrationMethods = useInvitationRegistration();
  const invitationMethods = useInvitations();

  const combinedMethods: InvitationContextType = {
    ...registrationMethods,
    ...invitationMethods
  };

  return (
    <InvitationContext.Provider value={combinedMethods}>
      {children}
    </InvitationContext.Provider>
  );
};
